import React, { createContext, useContext, useState, useEffect } from 'react';
import { Property } from '../types';

interface CompareContextType {
  comparedProperties: Property[];
  addToCompare: (property: Property) => boolean;
  removeFromCompare: (propertyId: string) => void;
  clearCompare: () => void;
  isComparing: (propertyId: string) => boolean;
  isCompareModalOpen: boolean;
  openCompareModal: () => void;
  closeCompareModal: () => void;
}

const CompareContext = createContext<CompareContextType | undefined>(undefined);

const STORAGE_KEY = 'clm_compared_properties';
export const MAX_COMPARE_LIMIT = 4;

export const CompareProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [comparedProperties, setComparedProperties] = useState<Property[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed.slice(0, MAX_COMPARE_LIMIT);
        }
      }
    } catch (e) {
      console.warn('Failed to parse compared properties from localStorage:', e);
    }
    return [];
  });

  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(comparedProperties));
    } catch (e) {
      console.warn('Failed to save compared properties to localStorage:', e);
    }
  }, [comparedProperties]);

  const addToCompare = (property: Property): boolean => {
    if (comparedProperties.some((p) => p.id === property.id)) {
      return true; // Already added
    }
    if (comparedProperties.length >= MAX_COMPARE_LIMIT) {
      return false; // Limit reached
    }
    setComparedProperties((prev) => [...prev, property]);
    return true;
  };

  const removeFromCompare = (propertyId: string) => {
    setComparedProperties((prev) => prev.filter((p) => p.id !== propertyId));
  };

  const clearCompare = () => {
    setComparedProperties([]);
  };

  const isComparing = (propertyId: string) => {
    return comparedProperties.some((p) => p.id === propertyId);
  };

  const openCompareModal = () => {
    setIsCompareModalOpen(true);
  };

  const closeCompareModal = () => {
    setIsCompareModalOpen(false);
  };

  return (
    <CompareContext.Provider
      value={{
        comparedProperties,
        addToCompare,
        removeFromCompare,
        clearCompare,
        isComparing,
        isCompareModalOpen,
        openCompareModal,
        closeCompareModal,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};
